import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNG } from 'primeng/config';
import { primeNgDateTranslation } from '../shared/format/primeng-locale';

export const DEFAULT_LOCALE = 'en';
export const SUPPORTED_LOCALES = ['en', 'ar'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const RTL_LOCALES: readonly string[] = ['ar'];

/** Applies a locale to both the translation service and the document's lang/dir attributes. */
@Injectable({ providedIn: 'root' })
export class LocaleService {
  private readonly translate = inject(TranslateService);
  private readonly primeng = inject(PrimeNG);

  constructor() {
    // The browser tab is titled with the product name in the current language ("Masharef" / «مشارف»); index.html carries
    // the English name for the moment before the first translations arrive.
    const setTitle = () => {
      const name = this.translate.instant('shared.logoAlt');
      if (typeof name === 'string' && name && name !== 'shared.logoAlt') document.title = name;
    };
    this.translate.onLangChange.subscribe(setTitle);
    this.translate.onTranslationChange.subscribe(setTitle);
  }

  readonly supportedLocales = SUPPORTED_LOCALES;

  apply(locale: string | null | undefined): void {
    const resolved = (SUPPORTED_LOCALES as readonly string[]).includes(locale ?? '') ? (locale as string) : DEFAULT_LOCALE;
    this.translate.use(resolved);
    // Every PrimeNG date picker: the locale's month and day names, Sunday first.
    this.primeng.setTranslation(primeNgDateTranslation(resolved));
    document.documentElement.lang = resolved;
    document.documentElement.dir = RTL_LOCALES.includes(resolved) ? 'rtl' : 'ltr';
  }
}
